import type { KoshLabels, KoshVertical } from '@/types/kosh';

/**
 * FE copy of the vertical terminology presets (backend source of truth:
 * runtime kosh-packaging/vertical-presets.ts). Used as the label fallback
 * when a tenant has a packaging profile but a given label key is unset, and
 * as the default before the profile loads. "One capability, many packagings":
 * the same screens relabel per the tenant's business template.
 */
export const VERTICAL_LABELS: Record<KoshVertical, KoshLabels> = {
  COMMERCE: { customer: 'Customer', contract: 'Order', invoice: 'Invoice', proposal: 'Quote' },
  PROFESSIONAL_SERVICES: {
    customer: 'Client',
    contract: 'Engagement',
    invoice: 'Invoice',
    proposal: 'Proposal',
  },
  REAL_ESTATE: { customer: 'Buyer', contract: 'Booking', invoice: 'Demand Note', proposal: 'Offer' },
  RECOVERY: {
    customer: 'Debtor',
    contract: 'Recovery Mandate',
    invoice: 'Statement',
    proposal: 'Plan',
  },
  SOFTWARE: {
    customer: 'Client',
    // 'Agreement', not 'Subscription' — Kosh now has a dedicated Subscriptions
    // page (recurring billing + ZAFROI subscription mirrors), so relabeling
    // Contracts to "Subscription" produced two identical nav items.
    contract: 'Agreement',
    invoice: 'Invoice',
    proposal: 'Quote',
  },
};

export const VERTICAL_NAMES: Record<KoshVertical, string> = {
  COMMERCE: 'Commerce',
  PROFESSIONAL_SERVICES: 'Professional Services',
  REAL_ESTATE: 'Real Estate',
  RECOVERY: 'Recovery Operations',
  SOFTWARE: 'Software & Technology',
};

/** Neutral default before any profile is known (generic SaaS terminology). */
export const DEFAULT_LABELS: KoshLabels = VERTICAL_LABELS.COMMERCE;

/** Pluralize a label simply (label maps are short, human nouns). */
export function pluralize(word: string): string {
  if (/[^aeiou]y$/i.test(word)) return word.replace(/y$/i, 'ies');
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
}
